import React, { useEffect, useState, useMemo } from "react";
import api, { getErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, getDay, isSameDay, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, X, Check } from "lucide-react";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS_EN = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Map our weekday name → JS getDay() index (Sun=0, Mon=1, …, Sat=6)
const DAY_TO_JS_INDEX = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };

// Stable per-program colour assignment.
const PROGRAM_PALETTE = [
  "bg-rose-50 border-rose-300 text-rose-800",
  "bg-amber-50 border-amber-300 text-amber-800",
  "bg-emerald-50 border-emerald-300 text-emerald-800",
  "bg-sky-50 border-sky-300 text-sky-800",
  "bg-violet-50 border-violet-300 text-violet-800",
  "bg-teal-50 border-teal-300 text-teal-800",
  "bg-indigo-50 border-indigo-300 text-indigo-800",
  "bg-fuchsia-50 border-fuchsia-300 text-fuchsia-800",
];

function programToneFor(programs, program) {
  const idx = programs.indexOf(program);
  if (idx < 0) return PROGRAM_PALETTE[0];
  return PROGRAM_PALETTE[idx % PROGRAM_PALETTE.length];
}

export default function CourseCalendarPanel() {
  const [cursor, setCursor] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const start = format(startOfMonth(cursor), "yyyy-MM-dd");
      const end = format(endOfMonth(cursor), "yyyy-MM-dd");
      const [sRes, cRes] = await Promise.all([
        api.get("/schedules").catch(() => ({ data: [] })),
        api.get(`/calendar?start=${start}&end=${end}`).catch(() => ({ data: { holidays: [], events: [] } })),
      ]);
      setSchedules(sRes.data || []);
      setHolidays(cRes.data?.holidays || []);
      setEvents(cRes.data?.events || []);
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { (async () => { await load(); })(); }, [cursor]);

  const days = useMemo(() => eachDayOfInterval({ start: startOfMonth(cursor), end: endOfMonth(cursor) }), [cursor]);
  const leadingBlanks = useMemo(() => {
    // We want Mon-first layout. JS Sunday=0, Monday=1 ... shift so Mon=0.
    const firstDow = getDay(startOfMonth(cursor)); // 0..6 (Sun..Sat)
    return (firstDow + 6) % 7; // 0..6 with Mon=0
  }, [cursor]);

  const programs = useMemo(() => Array.from(new Set(schedules.map(s => s.program))).sort(), [schedules]);

  function classesForDate(date) {
    const jsDay = getDay(date); // 0..6 Sun..Sat
    const iso = format(date, "yyyy-MM-dd");
    return schedules.filter((s) => {
      if (s.status === "suspended") return false;
      const days = (s.days_of_week && s.days_of_week.length) ? s.days_of_week : (s.day_of_week ? [s.day_of_week] : []);
      const matchesDay = days.some((d) => DAY_TO_JS_INDEX[d] === jsDay);
      if (!matchesDay) return false;
      // Honour the class's start/end window — only show on days inside [start_date, end_date].
      if (s.start_date && iso < s.start_date) return false;
      if (s.end_date && iso > s.end_date) return false;
      // Honour single-date cancellations.
      if (Array.isArray(s.skip_dates) && s.skip_dates.includes(iso)) return false;
      return true;
    });
  }
  function holidayOn(date) {
    const iso = format(date, "yyyy-MM-dd");
    return holidays.find(h => h.date === iso) || null;
  }
  function eventOn(date) {
    const iso = format(date, "yyyy-MM-dd");
    return events.find(e => (e.date <= iso) && ((e.end_date || e.date) >= iso)) || null;
  }

  const [openDayISO, setOpenDayISO] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  function openDay(d) {
    setOpenDayISO(format(d, "yyyy-MM-dd"));
    setEditingId(null);
  }

  function startEdit(s) {
    setEditingId(s.id);
    setDraft({ name: s.name, teacher: s.teacher || "", time_slot: s.time_slot || "", seats_total: s.seats_total ?? 0, seats_taken: s.seats_taken ?? 0, status: s.status || "active" });
  }

  async function saveEdit() {
    try {
      await api.patch(`/schedules/${editingId}`, { ...draft, seats_total: Number(draft.seats_total)||0, seats_taken: Number(draft.seats_taken)||0 });
      toast.success("Class updated");
      setEditingId(null);
      await load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  async function deleteClass(s) {
    if (!window.confirm(`Delete "${s.name}" from the schedule?`)) return;
    try { await api.delete(`/schedules/${s.id}`); toast.success("Class deleted"); await load(); }
    catch (e) { toast.error(getErrorDetail(e)); }
  }

  async function skipThisOccurrence(s, iso) {
    if (!window.confirm(`Cancel "${s.name}" for ${iso} only? Other dates will still run.`)) return;
    try {
      const next = Array.from(new Set([...(s.skip_dates || []), iso]));
      await api.patch(`/schedules/${s.id}`, { skip_dates: next });
      toast.success(`Cancelled for ${iso}`);
      await load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  async function duplicateClass(s) {
    try {
      const payload = {
        name: `${s.name} (copy)`, name_zh: s.name_zh || "", program: s.program,
        teacher: s.teacher || "", days_of_week: s.days_of_week || (s.day_of_week ? [s.day_of_week] : []),
        time_slot: s.time_slot || "", level: s.level || "Intermediate",
        seats_total: s.seats_total || 0, seats_taken: 0,
        status: s.status || "active",
        start_date: s.start_date, end_date: s.end_date,
      };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      await api.post("/schedules", payload);
      toast.success("Class duplicated");
      await load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  async function moveOffDay(s, fromISO) {
    const toISO = window.prompt(`Move "${s.name}" from this day. Enter the new date (YYYY-MM-DD):`, fromISO);
    if (!toISO || toISO === fromISO) return;
    const fromDow = DAY_NAMES[((parseISO(fromISO).getDay() + 6) % 7)];
    const toDow = DAY_NAMES[((parseISO(toISO).getDay() + 6) % 7)];
    const currentDays = s.days_of_week && s.days_of_week.length ? [...s.days_of_week] : (s.day_of_week ? [s.day_of_week] : []);
    const next = currentDays.filter(d => d !== fromDow);
    if (!next.includes(toDow)) next.push(toDow);
    try {
      await api.patch(`/schedules/${s.id}`, { days_of_week: next });
      toast.success(`Moved from ${fromDow} → ${toDow}`);
      await load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  const NEW_EMPTY = { name: "", name_zh: "", program: "K-12", teacher: "", time_slot: "", level: "Intermediate", seats_total: 8, seats_taken: 0, status: "active" };
  const [addingDraft, setAddingDraft] = useState(null); // null | object

  function startAddForDay() {
    if (!openDayISO) return;
    setAddingDraft({ ...NEW_EMPTY });
    setEditingId(null);
  }

  async function saveNewClass() {
    if (!addingDraft?.name || !addingDraft?.time_slot) { toast.error("Class name and time slot are required"); return; }
    const dow = DAY_NAMES[((parseISO(openDayISO).getDay() + 6) % 7)];
    try {
      await api.post("/schedules", {
        ...addingDraft,
        days_of_week: [dow],
        seats_total: Number(addingDraft.seats_total) || 0,
        seats_taken: Number(addingDraft.seats_taken) || 0,
        start_date: openDayISO,
      });
      toast.success(`Class added for ${dow}`);
      setAddingDraft(null);
      await load();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  const openDay_date = openDayISO ? parseISO(openDayISO) : null;
  const openDay_classes = openDay_date ? classesForDate(openDay_date) : [];
  const openDay_holiday = openDay_date ? holidayOn(openDay_date) : null;
  const openDay_event = openDay_date ? eventOn(openDay_date) : null;

  return (
    <div data-testid="admin-course-calendar" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-2xl tracking-tight">{format(cursor, "MMMM yyyy")}</h3>
          <p className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mt-1">{loading ? "Loading…" : `${schedules.length} classes loaded`}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={()=>setCursor(c => subMonths(c, 1))} data-testid="cal-prev-month" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={()=>setCursor(new Date())} data-testid="cal-today" className="rounded-full">Today</Button>
          <Button size="sm" variant="outline" onClick={()=>setCursor(c => addMonths(c, 1))} data-testid="cal-next-month" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
        {programs.map(p => (
          <span key={p} className={`px-2 py-0.5 rounded-full border ${programToneFor(programs, p)}`}>{p}</span>
        ))}
        <span className="px-2 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-700">national holiday</span>
        <span className="px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700">studio off / activity</span>
        <span className="px-2 py-0.5 rounded-full border border-sky-300 bg-sky-50 text-sky-700">planned (start date)</span>
      </div>

      <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))] pb-2">
        {DAY_LABELS_EN.map(d => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: leadingBlanks }).map((_, i) => <div key={`b-${i}`} />)}
        {days.map((d) => {
          const isToday = isSameDay(d, new Date());
          const cls = classesForDate(d);
          const h = holidayOn(d);
          const ev = eventOn(d);
          const isOffDay = ev && ev.type === "off_day";
          return (
            <button
              type="button"
              key={d.toISOString()}
              onClick={()=>openDay(d)}
              data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}
              className={`relative text-left min-h-[110px] rounded-lg border p-1.5 text-xs flex flex-col gap-1 transition-colors ${isToday ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' : isOffDay || h ? 'border-red-200 bg-red-50/30 hover:bg-red-50/60' : 'border-[hsl(var(--border))] bg-white hover:bg-[hsl(var(--primary))]/[0.04]'}`}
            >
              <div className="flex items-center justify-between">
                <span className={`font-medium ${isToday ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--foreground))]/80'}`}>{format(d, "d")}</span>
                {(h || ev) && (
                  <span className={`text-[9px] uppercase tracking-[0.15em] ${ev?.type === "activity" ? 'text-amber-600' : 'text-red-600'}`}>
                    {h ? (h.title_zh || h.title) : (ev?.type === "off_day" ? "off" : "activity")}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 overflow-hidden">
                {cls.slice(0, 4).map((s) => (
                  <div
                    key={s.id}
                    data-testid={`cal-class-${s.id}-${format(d, "yyyy-MM-dd")}`}
                    className={`px-1.5 py-1 rounded border text-[10px] leading-tight ${programToneFor(programs, s.program)} ${s.status === "planned" ? "ring-1 ring-sky-400" : ""}`}
                    title={`${s.name} · ${s.teacher} · ${s.time_slot}${s.status === "planned" ? " (planned)" : ""}`}
                  >
                    <div className="font-medium truncate">{s.name}</div>
                    <div className="opacity-70 truncate">{s.time_slot}</div>
                  </div>
                ))}
                {cls.length > 4 && <div className="text-[9px] text-[hsl(var(--muted-foreground))]">+{cls.length - 4} more</div>}
              </div>
            </button>
          );
        })}
      </div>

      {openDayISO && (
        <div data-testid="cal-day-drawer" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={()=>setOpenDayISO(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">Day overview</p>
                <h3 className="font-display text-2xl mt-1">{openDay_date ? format(openDay_date, "EEEE · MMMM d, yyyy") : ""}</h3>
                {(openDay_holiday || openDay_event) && (
                  <p className="text-xs mt-1 text-rose-700">
                    {openDay_holiday ? `🇨🇳 ${openDay_holiday.title} · ${openDay_holiday.title_zh}` : ""}
                    {openDay_event ? ` ${openDay_event.type === "off_day" ? "Studio closed" : "Activity"} · ${openDay_event.title}` : ""}
                  </p>
                )}
              </div>
              <Button size="sm" variant="ghost" onClick={()=>setOpenDayISO(null)} data-testid="cal-day-drawer-close"><X className="w-4 h-4" /></Button>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">Live classes · {openDay_classes.length}</p>
                <Button size="sm" onClick={startAddForDay} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid="cal-drawer-add-class">+ Add class for this day</Button>
              </div>
              {addingDraft && (
                <div className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5 p-4" data-testid="cal-drawer-add-form">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <div className="md:col-span-6"><Label className="text-[10px]">Class name</Label><Input value={addingDraft.name} onChange={(e)=>setAddingDraft({...addingDraft, name: e.target.value})} className="h-8 text-sm mt-0.5" data-testid="cal-add-name" /></div>
                    <div className="md:col-span-3"><Label className="text-[10px]">Program</Label>
                      <Select value={addingDraft.program} onValueChange={(v)=>setAddingDraft({...addingDraft, program: v})}>
                        <SelectTrigger className="h-8 mt-0.5" data-testid="cal-add-program"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {programs.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          {!programs.includes("K-12") && <SelectItem value="K-12">K-12</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3"><Label className="text-[10px]">Teacher</Label><Input value={addingDraft.teacher} onChange={(e)=>setAddingDraft({...addingDraft, teacher: e.target.value})} className="h-8 text-sm mt-0.5" data-testid="cal-add-teacher" /></div>
                    <div className="md:col-span-4"><Label className="text-[10px]">Time slot</Label><Input value={addingDraft.time_slot} onChange={(e)=>setAddingDraft({...addingDraft, time_slot: e.target.value})} placeholder="16:00 - 17:30" className="h-8 text-sm mt-0.5" data-testid="cal-add-time" /></div>
                    <div className="md:col-span-2"><Label className="text-[10px]">Level</Label><Input value={addingDraft.level} onChange={(e)=>setAddingDraft({...addingDraft, level: e.target.value})} className="h-8 text-sm mt-0.5" data-testid="cal-add-level" /></div>
                    <div className="md:col-span-2"><Label className="text-[10px]">Seats total</Label><Input type="number" value={addingDraft.seats_total} onChange={(e)=>setAddingDraft({...addingDraft, seats_total: e.target.value})} className="h-8 text-sm mt-0.5" data-testid="cal-add-total" /></div>
                    <div className="md:col-span-2"><Label className="text-[10px]">Status</Label>
                      <Select value={addingDraft.status} onValueChange={(v)=>setAddingDraft({...addingDraft, status: v})}>
                        <SelectTrigger className="h-8 mt-0.5" data-testid="cal-add-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="planned">Planned</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2 flex items-end gap-2">
                      <Button size="sm" variant="outline" onClick={()=>setAddingDraft(null)} className="h-8 rounded-full flex-1" data-testid="cal-add-cancel">Cancel</Button>
                      <Button size="sm" onClick={saveNewClass} className="h-8 rounded-full flex-1 bg-[hsl(var(--foreground))] text-[hsl(var(--background))]" data-testid="cal-add-save">Save</Button>
                    </div>
                  </div>
                </div>
              )}
              {openDay_classes.length === 0 && !addingDraft && <p className="text-sm italic text-[hsl(var(--muted-foreground))]">No classes scheduled for this day.</p>}
              {openDay_classes.map((s) => (
                <div key={s.id} data-testid={`cal-drawer-class-${s.id}`} className={`rounded-xl border p-4 ${programToneFor(programs, s.program)}`}>
                  {editingId === s.id ? (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 bg-white/70 p-3 rounded-lg">
                      <div className="md:col-span-5"><Label className="text-[10px]">Class name</Label><Input value={draft.name} onChange={(e)=>setDraft({...draft, name: e.target.value})} className="h-8 text-sm mt-0.5" data-testid={`cal-edit-${s.id}-name`} /></div>
                      <div className="md:col-span-3"><Label className="text-[10px]">Teacher</Label><Input value={draft.teacher} onChange={(e)=>setDraft({...draft, teacher: e.target.value})} className="h-8 text-sm mt-0.5" data-testid={`cal-edit-${s.id}-teacher`} /></div>
                      <div className="md:col-span-4"><Label className="text-[10px]">Time</Label><Input value={draft.time_slot} onChange={(e)=>setDraft({...draft, time_slot: e.target.value})} className="h-8 text-sm mt-0.5" data-testid={`cal-edit-${s.id}-time`} /></div>
                      <div className="md:col-span-3"><Label className="text-[10px]">Status</Label>
                        <Select value={draft.status} onValueChange={(v)=>setDraft({...draft, status: v})}>
                          <SelectTrigger className="h-8 mt-0.5" data-testid={`cal-edit-${s.id}-status`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="planned">Planned</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3"><Label className="text-[10px]">Seats taken</Label><Input type="number" value={draft.seats_taken} onChange={(e)=>setDraft({...draft, seats_taken: e.target.value})} className="h-8 text-sm mt-0.5" data-testid={`cal-edit-${s.id}-taken`} /></div>
                      <div className="md:col-span-3"><Label className="text-[10px]">Seats total</Label><Input type="number" value={draft.seats_total} onChange={(e)=>setDraft({...draft, seats_total: e.target.value})} className="h-8 text-sm mt-0.5" data-testid={`cal-edit-${s.id}-total`} /></div>
                      <div className="md:col-span-3 flex items-end justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={()=>setEditingId(null)} data-testid={`cal-edit-${s.id}-cancel`} className="h-8 rounded-full">Cancel</Button>
                        <Button size="sm" onClick={saveEdit} className="h-8 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid={`cal-edit-${s.id}-save`}><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.18em] opacity-70">{s.program} · {s.status}{s.status === "planned" && s.start_date ? ` from ${s.start_date}` : ""}{s.end_date ? ` until ${s.end_date}` : ""}</p>
                        <p className="font-display text-lg tracking-tight mt-0.5">{s.name}</p>
                        <p className="text-xs opacity-80 mt-0.5">{s.teacher} · {s.time_slot} · {s.seats_taken}/{s.seats_total} seats</p>
                      </div>
                      <div className="flex flex-wrap items-end justify-end gap-1.5">
                      <Button size="sm" variant="outline" onClick={()=>startEdit(s)} className="rounded-full h-8 text-xs bg-white" data-testid={`cal-drawer-edit-${s.id}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={()=>duplicateClass(s)} className="rounded-full h-8 text-xs bg-white" data-testid={`cal-drawer-duplicate-${s.id}`}>Duplicate</Button>
                      <Button size="sm" variant="outline" onClick={()=>moveOffDay(s, openDayISO)} className="rounded-full h-8 text-xs bg-white" data-testid={`cal-drawer-move-${s.id}`}>Move</Button>
                      <Button size="sm" variant="outline" onClick={()=>skipThisOccurrence(s, openDayISO)} className="rounded-full h-8 text-xs bg-white text-amber-700 border-amber-300" data-testid={`cal-drawer-skip-${s.id}`} title="Cancel this date only">Delete only this</Button>
                      <Button size="sm" variant="ghost" onClick={()=>deleteClass(s)} className="rounded-full h-8 text-xs text-[hsl(var(--destructive))]" data-testid={`cal-drawer-delete-${s.id}`} title="Delete all occurrences">Delete all</Button>
                    </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
